// @pages/document/Refine.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeReviewWithDocument } from "@navigation/typedRoutes";
import { useContentClient } from '@contexts/ContentClient.context';
import { useDocumentContext } from '@contexts/Document.context';
import type { ContentError, RefineRequest, RefineResponse, Section } from "@ps-bridge/models/core/dataContracts";
import type { DocumentID } from "@ps-bridge/models/brands/documentID";
import type { SectionID } from "@ps-bridge/models/brands/sectionID";
import { useLoadingController } from '@contexts/Loading.context';
import { useIoOptionState } from '@utils/useIoOptionState';

import { ok, type Ok } from "@ps-bridge/ok";
import type { OptionTs, TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { StateUpdate } from "@ps-bridge/capabilitiesTypes";
import type { EmitOnRouteChange } from "@ps-bridge/capabilitiesTypes";
import { ioEffect, ioMap, taskEitherFromEffect, taskEitherMap } from '@utils/effects/branded';
import * as Option from "@utils/Option";
import { optionToPsMaybe } from "@src/purescript/interop";
import { useOnRouteChange } from "@hooks/effects/useIoEffect";
import { cleanupNone, useIoEffectWithCleanup } from "@hooks/effects/useIoEffectWithCleanup";
import { mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { RefineSectionFetch } from "@ps-bridge/contracts/documentRefine";
import type { DocumentRefineKernel } from "@ps-bridge/contracts/documentRefine";
import { buildDocumentRefineViewProps } from "@ps-bridge/documentRefine.program";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import RefinePagePanel, {
    type RefinePagePhase,
} from "@view/panels/document/RefinePage.panel";
import { getParamOption } from "@utils/searchParams";
import { makeSectionID } from "@ps-bridge/models/brands/sectionID";
import { readOptionString } from "@utils/readOptionString";

const toPsMaybe = <A,>(value: OptionTs<A>): OptionTs<A> => optionToPsMaybe(value);

const normalizeSectionForKernel = (section: Section): Section => ({
    ...section,
    metadata: {
        ...section.metadata,
        last_edited: toPsMaybe(section.metadata.last_edited),
    },
    section_contents: toPsMaybe(section.section_contents),
});

const normalizeRefineResponse = (response: RefineResponse): RefineResponse => ({
    ...response,
    updated_section: normalizeSectionForKernel(response.updated_section),
});

const RefinePage: React.FC = () => {
    const navigate = useNavigate();
    const contentClient = useContentClient();
    const { activeSection, controller, document } = useDocumentContext();
    const [searchParams] = useSearchParams();
    const documentOption = document.read;
    const {
        read: mostRecentSectionRead,
        setMaybe: setMostRecentSectionMaybe,
        setSome: setMostRecentSectionSome,
    } = useIoOptionState<Section>(Option.none());
    const mostRecentSectionHandle = useMemo(
        () => ({
            read: mostRecentSectionRead,
            setMaybe: setMostRecentSectionMaybe,
            setSome: setMostRecentSectionSome,
        }),
        [mostRecentSectionRead, setMostRecentSectionMaybe, setMostRecentSectionSome],
    );

    const loading = useLoadingController();
    const routeSectionId: OptionTs<SectionID> = useMemo(
        () =>
            Option.fold(
                getParamOption(searchParams, "section_id"),
                () => Option.none(),
                (value) => Option.some(makeSectionID(value)),
            ),
        [searchParams],
    );
    const syncRouteSectionOnRouteChange = useMemo(
        () =>
            ((nextRouteSectionId: OptionTs<SectionID>) =>
                Option.fold(
                    nextRouteSectionId,
                    () => ioEffect(() => ok),
                    (sectionId) =>
                        Option.fold(
                            activeSection.read,
                            () => activeSection.setSome(sectionId),
                            (value) => (value === sectionId ? ioEffect(() => ok) : activeSection.setSome(sectionId)),
                        ),
                )) as EmitOnRouteChange<OptionTs<SectionID>>,
        [activeSection],
    );
    useOnRouteChange(routeSectionId, syncRouteSectionOnRouteChange);
    const syncMostRecentSectionSetup = useMemo(
        () => {
            const selectedSectionId = Option.fold(
                routeSectionId,
                () => activeSection.read,
                (sectionId) => Option.some(sectionId),
            );
            const mostRecentSection = Option.fold(
                documentOption,
                () => Option.none<Section>(),
                (userDocument) => {
                    const fallbackSection = () => {
                        const sorted = [...userDocument.document_contents].sort(
                            (a, b) => a.metadata.index - b.metadata.index,
                        );
                        return sorted.find(
                            (section) => readOptionString(section.section_contents).trim().length > 0,
                        ) ?? sorted[0];
                    };
                    const canFallback = Option.fold(routeSectionId, () => true, () => false);
                    const section = Option.fold(
                        selectedSectionId,
                        () => (canFallback ? fallbackSection() : undefined),
                        (sectionId) =>
                            userDocument.document_contents.find(
                                (sec) => sec.metadata.id === sectionId,
                            ) ?? (canFallback ? fallbackSection() : undefined),
                    );
                    return section ? Option.some(section) : Option.none<Section>();
                },
            );
            return ioMap(
                setMostRecentSectionMaybe(mostRecentSection),
                () => cleanupNone(),
            );
        },
        [activeSection.read, documentOption, routeSectionId, setMostRecentSectionMaybe],
    );
    useIoEffectWithCleanup(syncMostRecentSectionSetup, [syncMostRecentSectionSetup]);
    const runTaskWithError = useRunTaskWithError();
    const runTaskWithErrorIo = useCallback(
        (task: TaskTs<Ok>) =>
            ioEffect(() => {
                runTaskWithError(task).then(() => ok);
                return ok;
            }),
        [runTaskWithError],
    );
    const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);
    const refineSectionFetch: RefineSectionFetch = useMemo(() => {
        const invoke = (
            documentId: DocumentID,
            request: RefineRequest,
        ): TaskEitherTs<ContentError, RefineResponse> =>
            taskEitherMap(
                taskEitherFromEffect(
                    contentClient.refineSection(documentId, request),
                    (reason) => reason as ContentError,
                ),
                normalizeRefineResponse,
            );

        function fetch(documentId: DocumentID, request: RefineRequest): ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID): (request: RefineRequest) => ReturnType<typeof invoke>;
        function fetch(documentId: DocumentID, request?: RefineRequest) {
            if (request === undefined) {
                return (nextRequest: RefineRequest) => invoke(documentId, nextRequest);
            }
            return invoke(documentId, request);
        }

        return Object.assign(fetch, {
            __brandRefineSectionFetch: "RefineSectionFetch" as const,
        }) as RefineSectionFetch;
    }, [contentClient]);
    const kernel: DocumentRefineKernel = useMemo(
        () => ({
            loading,
            navigate: mkNavigate((route) =>
                ioEffect(() => {
                    navigate(renderRoute(route), { replace: true });
                    return ok;
                }),
            ),
            reviewRoute: mkRouteBuilder(routeReviewWithDocument),
            document,
            mostRecentSection: mostRecentSectionHandle,
            updateSection: controller.updateSection as StateUpdate<Section>,
            refineSection: refineSectionFetch,
            taskRunner,
        }),
        [
            controller.updateSection,
            document,
            loading,
            mostRecentSectionHandle,
            navigate,
            refineSectionFetch,
            taskRunner,
        ],
    );
    const viewProps = useMemo(
        () =>
            buildDocumentRefineViewProps({
                kernel,
            }),
        [kernel],
    );

    const phase: RefinePagePhase = Option.fold(
        documentOption,
        () => "missing",
        () => "ready",
    );

    return <RefinePagePanel phase={phase} viewProps={viewProps} />;
};

export default RefinePage;
